import React from 'react';
import passwordIcon from '../../assets/password.png';

/**
 * Small inline-SVG illustrations for each wizard step. Kept as plain shapes (no external
 * images) so they stay crisp at any size and never add a network request.
 */

const ART_COLORS = {
  brand: '#2563eb',
  brandLight: '#dbeafe',
  brandFaint: '#eff6ff',
  success: '#16a34a',
  successLight: '#dcfce7',
  warn: '#d97706',
  warnLight: '#fef3c7',
  ink: '#1e293b',
  muted: '#94a3b8',
  line: '#cbd5e1',
};

const Frame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg viewBox="0 0 240 150" width="100%" height="100%" role="img" aria-hidden="true">
    {children}
  </svg>
);

export const ChooseProviderArt: React.FC = () => (
  <Frame>
    {[
      { x: 20, active: true, label: 'Hostex' },
      { x: 92, active: false, label: 'Guesty' },
      { x: 164, active: false, label: 'Smoobu' },
    ].map((card) => (
      <g key={card.label} opacity={card.active ? 1 : 0.45}>
        <rect
          x={card.x}
          y={40}
          width={56}
          height={70}
          rx={10}
          fill={card.active ? ART_COLORS.brandFaint : '#f1f5f9'}
          stroke={card.active ? ART_COLORS.brand : ART_COLORS.line}
          strokeWidth={card.active ? 2 : 1.5}
        />
        <circle cx={card.x + 28} cy={64} r={12} fill={card.active ? ART_COLORS.brand : ART_COLORS.muted} />
        {card.active && (
          <path
            d={`M ${card.x + 22} 64 l 4 4 l 8 -8`}
            stroke="#fff"
            strokeWidth={2.4}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        <text x={card.x + 28} y={100} fontSize={13} textAnchor="middle" fill={ART_COLORS.ink} fontWeight={700}>
          {card.label}
        </text>
      </g>
    ))}
  </Frame>
);

export const ApiKeyArt: React.FC = () => (
  <Frame>
    {/* Hostex (left) */}
    <rect x={8} y={60} width={84} height={48} rx={10} fill="#fdf4ff" stroke="#a855f7" strokeWidth={1.5} />
    <text x={50} y={89} fontSize={14} textAnchor="middle" fill="#7e22ce" fontWeight={700}>
      Hostex
    </text>

    {/* Guestbook (right) */}
    <rect x={146} y={60} width={84} height={48} rx={10} fill={ART_COLORS.brandFaint} stroke={ART_COLORS.brand} strokeWidth={1.5} />
    <text x={188} y={89} fontSize={14} textAnchor="middle" fill={ART_COLORS.brand} fontWeight={700}>
      Guestbook
    </text>

    {/* The API key, travelling from Hostex to Guestbook */}
    <path
      d="M 94 82 C 114 58, 126 58, 146 82"
      stroke={ART_COLORS.muted}
      strokeWidth={1.8}
      fill="none"
      strokeDasharray="4 4"
      markerEnd="url(#arrowKey)"
    />
    <defs>
      <marker id="arrowKey" markerWidth={7} markerHeight={7} refX={5} refY={3.5} orient="auto">
        <path d="M0,0 L7,3.5 L0,7 Z" fill={ART_COLORS.muted} />
      </marker>
    </defs>
    <image href={passwordIcon} x={88} y={2} width={64} height={64} />
  </Frame>
);

export const WebhookArt: React.FC = () => (
  <Frame>
    <rect x={10} y={58} width={86} height={48} rx={10} fill={ART_COLORS.brandFaint} stroke={ART_COLORS.brand} strokeWidth={1.5} />
    <text x={53} y={87} fontSize={14} textAnchor="middle" fill={ART_COLORS.brand} fontWeight={700}>
      Guestbook
    </text>

    <rect x={144} y={58} width={86} height={48} rx={10} fill="#fdf4ff" stroke="#a855f7" strokeWidth={1.5} />
    <text x={187} y={87} fontSize={14} textAnchor="middle" fill="#7e22ce" fontWeight={700}>
      Hostex
    </text>

    <path
      d="M 98 80 C 118 56, 122 56, 142 80"
      stroke={ART_COLORS.muted}
      strokeWidth={1.8}
      fill="none"
      strokeDasharray="4 4"
      markerEnd="url(#arrow2)"
    />
    <defs>
      <marker id="arrow2" markerWidth={7} markerHeight={7} refX={5} refY={3.5} orient="auto">
        <path d="M0,0 L7,3.5 L0,7 Z" fill={ART_COLORS.muted} />
      </marker>
    </defs>
    <rect x={70} y={20} width={100} height={26} rx={13} fill="#fff" stroke={ART_COLORS.line} strokeWidth={1.3} />
    <text x={120} y={37} fontSize={12} textAnchor="middle" fill={ART_COLORS.ink} fontWeight={600}>
      webhook URL
    </text>
  </Frame>
);

export const TestReservationArt: React.FC = () => (
  <Frame>
    <rect x={64} y={22} width={112} height={96} rx={10} fill="#fff" stroke={ART_COLORS.line} strokeWidth={1.5} />
    <rect x={64} y={22} width={112} height={22} rx={10} fill={ART_COLORS.brand} />
    <rect x={64} y={34} width={112} height={10} fill={ART_COLORS.brand} />
    {[0, 1, 2, 3].map((col) =>
      [0, 1, 2].map((row) => {
        const isTarget = col === 2 && row === 1;
        return (
          <rect
            key={`${col}-${row}`}
            x={74 + col * 24}
            y={54 + row * 20}
            width={18}
            height={14}
            rx={3}
            fill={isTarget ? ART_COLORS.warn : '#f1f5f9'}
          />
        );
      })
    )}
    <circle cx={176} cy={110} r={16} fill={ART_COLORS.successLight} stroke={ART_COLORS.success} strokeWidth={2} />
    <path d="M 169 110 l 5 5 l 10 -11" stroke={ART_COLORS.success} strokeWidth={2.4} fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </Frame>
);

export const DoneArt: React.FC = () => (
  <Frame>
    <circle cx={120} cy={70} r={38} fill={ART_COLORS.successLight} stroke={ART_COLORS.success} strokeWidth={2.5} />
    <path
      d="M 102 70 l 13 13 l 26 -28"
      stroke={ART_COLORS.success}
      strokeWidth={5}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {[
      [30, 30], [210, 34], [26, 112], [214, 108], [120, 14], [120, 128],
    ].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r={i % 2 === 0 ? 3.5 : 2.5} fill={i % 3 === 0 ? ART_COLORS.brand : ART_COLORS.warn} opacity={0.8} />
    ))}
  </Frame>
);
